import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { getMember, logout as logoutApi } from '@/api/auth';
import { canAccessPurchasePlan, clearAuth, getToken, getUser, isDivideAccount, redirectToLogin, setUser, type AuthUser } from '@/utils/auth';
import { alertError, alertSuccess } from '@/utils/message';

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

interface NavGroup {
  label: string;
  icon: string;
  items: NavItem[];
}

const purchaseGroup: NavGroup = {
  label: '采购管理',
  icon: 'ri-shopping-bag-line',
  items: [
    { path: '/orders', label: '订单管理', icon: 'ri-file-list-3-line' },
    { path: '/purchase-plan', label: '采购计划', icon: 'ri-calendar-check-line' },
    { path: '/after-sales', label: '我的售后', icon: 'ri-customer-service-line' },
    { path: '/proxy-order', label: '代购下单', icon: 'ri-user-received-line' },
    { path: '/frequent-purchase', label: '常购单', icon: 'ri-heart-3-line' },
    { path: '/cart', label: '购物车', icon: 'ri-shopping-cart-line' },
  ],
};

const topNavItems: NavItem[] = [
  { path: '/goods', label: '物资浏览', icon: 'ri-store-2-line' },
  { path: '/reports', label: '数据报表', icon: 'ri-bar-chart-2-line' },
];

const workApprovalItem: NavItem = {
  // path: '/work-approval',
  // label: '工作审批',
  // icon: 'ri-shield-check-line',
};

function Badge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center font-medium">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { totalCount } = useCart();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [flowsCount, setFlowsCount] = useState(0);
  const [purchasePlansCount, setPurchasePlansCount] = useState(0);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await getMember();
        if (cancelled) return;

        const ok = res.error === false || Number(res.error) === 0;
        if (ok && res.data) {
          setCurrentUser(res.data);
          const token = getToken() || undefined;
          setUser({
            ...res.data,
            token,
            is_canedit: res.data.is_canedit ?? res.is_canedit,
            is_divide: res.data.is_divide ?? res.is_divide,
          });
          const flows = Number(res.flows_count ?? 0);
          const plans = Number(res.purchase_plans_count ?? 0);
          setFlowsCount(Number.isFinite(flows) && flows > 0 ? flows : 0);
          setPurchasePlansCount(Number.isFinite(plans) && plans > 0 ? plans : 0);
        }
      } catch {
        // 498 等由请求拦截器统一处理
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const displayName = currentUser?.nickname || currentUser?.account || (userLoading ? '加载中...' : '用户');
  const displayDept =
    currentUser?.department?.title ||
    currentUser?.enterprise?.title ||
    currentUser?.position ||
    '';
  const showProxyOrder = isDivideAccount(currentUser ?? getUser());
  const showPurchasePlan = canAccessPurchasePlan(currentUser ?? getUser());

  const visiblePurchaseGroup: NavGroup = {
    ...purchaseGroup,
    items: purchaseGroup.items.filter((item) => {
      if (item.path === '/proxy-order') return showProxyOrder;
      if (item.path === '/purchase-plan') return showPurchasePlan;
      return true;
    }),
  };

  const itemBadge = (path: string) => {
    if (path === '/orders') return flowsCount;
    if (path === '/purchase-plan') return purchasePlansCount;
    if (path === '/cart') return totalCount;
    return 0;
  };

  /** 采购管理入口徽标：订单待办 + 采购计划待办（无采购计划权限时不计计划数） */
  const purchaseGroupBadge =
    flowsCount + (showPurchasePlan ? purchasePlansCount : 0);

  const handleLogout = async () => {
    if (loggingOut) return;
    setUserMenuOpen(false);
    setLoggingOut(true);
    try {
      const res = await logoutApi();
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        await alertError(res.message || res.msg || '退出失败，请稍后重试', '退出登录');
        return;
      }
      clearAuth();
      await alertSuccess(res.message || '注销成功', '退出登录');
      redirectToLogin({ replaceHistory: true });
    } catch {
      await alertError('网络异常，退出失败，请稍后重试', '退出登录');
    } finally {
      setLoggingOut(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/goods') {
      return location.pathname === '/goods' || location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isGroupActive = (group: NavGroup) => group.items.some((item) => isActive(item.path));

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/goods?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      let inDropdown = false;
      Object.values(dropdownRefs.current).forEach((ref) => {
        if (ref && ref.contains(target)) inDropdown = true;
      });
      if (userMenuRef.current && userMenuRef.current.contains(target)) inDropdown = true;
      if (!inDropdown) {
        setOpenDropdown(null);
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const renderNavLink = (item: NavItem, onClick?: () => void) => {
    const active = isActive(item.path);
    const badge = itemBadge(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${
          active
            ? 'bg-primary-50 text-primary-700 font-medium'
            : 'text-foreground-700 hover:bg-background-50 hover:text-foreground-900'
        }`}
        onClick={onClick}
      >
        <div className="w-5 h-5 flex items-center justify-center relative">
          <i className={item.icon} />
          {item.path === '/cart' && totalCount > 0 && (
            <span className="absolute -top-1 -right-1.5 min-w-3.5 h-3.5 px-0.5 bg-primary-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold leading-none">
              {totalCount > 99 ? '99+' : totalCount}
            </span>
          )}
        </div>
        <span className="flex-1">{item.label}</span>
        {item.path !== '/cart' && <Badge count={badge} />}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-background-200">
      <div className="flex items-center h-20 px-4 md:px-6 gap-4">
        <Link to="/goods" className="flex items-center gap-2.5 shrink-0 mr-4">
          <img
            src="https://cdn.yyg120.com/Fo_BZtyQ7IYqYrZdSexcrUnft1PI"
            alt="邕宜购"
            className="w-9 h-9 rounded-lg object-cover"
          />
          <div className="hidden sm:block">
            <h1 className="text-sm font-bold text-foreground-900 leading-tight">邕E购</h1>
            <p className="text-[10px] text-foreground-500">物资线上采购平台</p>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {topNavItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                <i className={item.icon} />
                <span>{item.label}</span>
              </Link>
            );
          })}

          {/* 采购管理 */}
          {(() => {
            const group = visiblePurchaseGroup;
            const groupActive = isGroupActive(group);
            const isOpen = openDropdown === group.label;
            return (
              <div
                key={group.label}
                className="relative"
                ref={(el) => {
                  dropdownRefs.current[group.label] = el;
                }}
                onMouseEnter={() => setOpenDropdown(group.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  type="button"
                  onClick={() => setOpenDropdown(isOpen ? null : group.label)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                    groupActive || isOpen
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <i className={group.icon} />
                  <span>{group.label}</span>
                  <Badge count={purchaseGroupBadge} />
                  <i
                    className={`ri-arrow-down-s-line text-xs transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isOpen && <div className="absolute top-full left-0 w-full h-1 z-40" />}

                {isOpen && (
                  <div className="absolute top-[calc(100%+0.25rem)] left-0 w-52 bg-white border border-background-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden">
                    {group.items.map((item) =>
                      renderNavLink(item, () => setOpenDropdown(null)),
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* 工作审批：采购管理右侧独立菜单 */}
          <Link
            to={workApprovalItem.path}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
              isActive(workApprovalItem.path)
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
            }`}
          >
            <i className={workApprovalItem.icon} />
            <span>{workApprovalItem.label}</span>
          </Link>
        </nav>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className="hidden lg:flex items-center gap-0 rounded-lg border border-background-200 bg-background-100 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100/50 transition-all w-64 xl:w-96 overflow-hidden"
          >
            <div className="flex items-center gap-2 px-3 py-2 flex-1 min-w-0">
              <i className="ri-search-line text-sm text-foreground-400 shrink-0" />
              <input
                type="text"
                placeholder="搜索物资..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 text-sm outline-none bg-transparent min-w-0"
              />
            </div>
            <button
              type="submit"
              className="shrink-0 h-full px-3.5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <Link
            to="/goods"
            className="lg:hidden w-8 h-8 rounded hover:bg-background-100 flex items-center justify-center text-foreground-500 hover:text-foreground-700 transition-colors"
          >
            <i className="ri-search-line text-sm" />
          </Link>

          <Link
            to="/cart"
            className="relative w-9 h-9 rounded hover:bg-background-100 flex items-center justify-center text-foreground-500 hover:text-foreground-700 transition-colors"
          >
            <i className="ri-shopping-cart-line text-lg" />
            {totalCount > 0 && (
              <span className="absolute top-0 right-0 w-4 h-4 bg-primary-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                {totalCount > 99 ? '99+' : totalCount}
              </span>
            )}
          </Link>

          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-line text-sm text-primary-600" />
              </div>
              <span className="text-sm text-foreground-700 hidden sm:block whitespace-nowrap">
                {displayName}
              </span>
              <i
                className={`ri-arrow-down-s-line text-xs text-foreground-400 hidden sm:block transition-transform ${
                  userMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {userMenuOpen && (
              <div className="absolute top-[calc(100%+0.25rem)] right-0 w-56 bg-white border border-background-200 rounded-lg shadow-lg z-50 py-1">
                <div className="px-4 py-3 border-b border-background-100">
                  <p className="text-sm font-medium text-foreground-900">{displayName}</p>
                  {displayDept ? (
                    <p className="text-xs text-foreground-500 mt-0.5">{displayDept}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  disabled={loggingOut}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-500 hover:bg-background-50 hover:text-foreground-700 transition-colors cursor-pointer disabled:opacity-60"
                  onClick={() => void handleLogout()}
                >
                  <i className={loggingOut ? 'ri-loader-4-line animate-spin' : 'ri-logout-box-r-line'} />
                  {loggingOut ? '退出中...' : '退出登录'}
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setOpenDropdown(openDropdown === 'mobile' ? null : 'mobile')}
            className="md:hidden w-8 h-8 rounded hover:bg-background-100 flex items-center justify-center text-foreground-500 hover:text-foreground-700 transition-colors"
          >
            <i className={`ri-${openDropdown === 'mobile' ? 'close' : 'menu'}-line text-sm`} />
          </button>
        </div>
      </div>

      {openDropdown === 'mobile' && (
        <div className="md:hidden border-t border-background-100 bg-white px-4 py-3 space-y-1">
          {topNavItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm ${
                  active
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
                onClick={() => setOpenDropdown(null)}
              >
                <i className={item.icon} />
                {item.label}
              </Link>
            );
          })}

          <div>
            <p className="px-3 py-1.5 text-[11px] font-semibold text-foreground-400 uppercase tracking-wider flex items-center gap-1.5">
              <span>{visiblePurchaseGroup.label}</span>
              <Badge count={purchaseGroupBadge} />
            </p>
            {visiblePurchaseGroup.items.map((item) => {
              const active = isActive(item.path);
              const badge = itemBadge(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm ${
                    active
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                  onClick={() => setOpenDropdown(null)}
                >
                  <i className={item.icon} />
                  <span className="flex-1">{item.label}</span>
                  {item.path === '/cart' ? (
                    totalCount > 0 ? (
                      <Badge count={totalCount} />
                    ) : null
                  ) : (
                    <Badge count={badge} />
                  )}
                </Link>
              );
            })}
          </div>

          <Link
            to={workApprovalItem.path}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm ${
              isActive(workApprovalItem.path)
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-foreground-700 hover:bg-background-100'
            }`}
            onClick={() => setOpenDropdown(null)}
          >
            <i className={workApprovalItem.icon} />
            {workApprovalItem.label}
          </Link>
        </div>
      )}
    </header>
  );
}
